/**
 * The Clients (Accounts) page's remembered view — filters, grouping, column sort.
 *
 * Same problem the Projects page had (feedback 04f37df4): the page dropped every filter on
 * unmount, so the team rebuilt their view on every visit. The last-used view is written to
 * localStorage on change and restored on mount. Named presets (FilterPresetBar) stay a
 * separate, explicit thing — this is the implicit "where I left off" memory.
 *
 * Everything read back is validated rather than trusted: the blob may have been written by an
 * older (or newer) build, or hand-edited in devtools, and there is no UI to clear it — so an
 * unrecognized value has to degrade to the default instead of wedging the page.
 *
 * Deliberately NOT stored:
 *  - searchQuery — a one-off lookup, not a view preference. Restoring it would reopen the page
 *    filtered to a single client with the reason buried in a text box (matches Projects).
 *  - currentPage / expandedGroups — position within a view, not the view itself.
 *
 * Unlike the Projects page, statusFilter IS stored here. There it was excluded because "all"
 * refetches every project ever and the server computes per-project aggregates for all of them;
 * on this page it only decides whether inactive clients are included in one flat account list,
 * so remembering it is cheap and it's the filter the team changes most.
 */

export type AccountsGroupBy = "none" | "status" | "assignee" | "workflow_role";
export type AccountsStatusFilter = "active" | "inactive" | "all";
export type AccountsSortCol = "client" | "status" | null;
export type SortDir = "asc" | "desc";

export interface StoredAccountsView {
  statusFilter?: AccountsStatusFilter;
  assigneeFilter?: string;
  workflowRoleFilter?: string;
  groupFilter?: string | null;
  groupBy?: AccountsGroupBy;
  sortCol?: AccountsSortCol;
  sortDir?: SortDir;
}

export const DEFAULT_STATUS_FILTER: AccountsStatusFilter = "active";
export const DEFAULT_GROUP_BY: AccountsGroupBy = "none";

export const ACCOUNTS_VIEW_KEY = "centcom_accounts_view";

const STATUS_FILTER_VALUES: AccountsStatusFilter[] = ["active", "inactive", "all"];
const GROUP_BY_VALUES: AccountsGroupBy[] = ["none", "status", "assignee", "workflow_role"];
const WORKFLOW_ROLE_VALUES = [
  "all",
  "account_manager",
  "preparer",
  "checker",
  "reviewer",
  "assembler",
  "signer",
];

export function sanitizeStoredView(raw: unknown): StoredAccountsView {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const v = raw as Record<string, unknown>;
  const view: StoredAccountsView = {};

  if (STATUS_FILTER_VALUES.includes(v.statusFilter as AccountsStatusFilter)) {
    view.statusFilter = v.statusFilter as AccountsStatusFilter;
  }
  // "all" | "unassigned" | a user id — the id list isn't known here, so any non-empty
  // string is accepted. A stale id simply matches no client, which the empty state explains.
  if (typeof v.assigneeFilter === "string" && v.assigneeFilter) view.assigneeFilter = v.assigneeFilter;
  if (typeof v.workflowRoleFilter === "string" && WORKFLOW_ROLE_VALUES.includes(v.workflowRoleFilter)) {
    view.workflowRoleFilter = v.workflowRoleFilter;
  }
  // A group id can outlive its group, and the picker hides its own "clear" control when no
  // groups exist — so the page drops an unknown id once the group list has loaded.
  if (v.groupFilter === null || (typeof v.groupFilter === "string" && v.groupFilter)) {
    view.groupFilter = v.groupFilter as string | null;
  }
  if (GROUP_BY_VALUES.includes(v.groupBy as AccountsGroupBy)) view.groupBy = v.groupBy as AccountsGroupBy;
  if (v.sortCol === null || v.sortCol === "client" || v.sortCol === "status") view.sortCol = v.sortCol;
  if (v.sortDir === "asc" || v.sortDir === "desc") view.sortDir = v.sortDir;

  return view;
}

export function loadPersistedAccountsView(): StoredAccountsView {
  let raw: unknown;
  try {
    const stored = localStorage.getItem(ACCOUNTS_VIEW_KEY);
    if (!stored) return {};
    raw = JSON.parse(stored);
  } catch {
    // corrupt json or storage unavailable
    return {};
  }
  return sanitizeStoredView(raw);
}

export function serializeAccountsView(view: StoredAccountsView): string | null {
  try {
    return JSON.stringify(view);
  } catch {
    return null;
  }
}

export function savePersistedAccountsView(json: string): void {
  try {
    localStorage.setItem(ACCOUNTS_VIEW_KEY, json);
  } catch {
    // quota exceeded, just do nothing
  }
}
